import { useState } from 'react'
import { errorMessage } from '../api/client'
import {
  useCategories,
  useChangePassword,
  useClaudeStatus,
  useDevices,
  useJobs,
  useLmLink,
  useMailAccountActions,
  useMailAccounts,
  useProviderActions,
  useProviders,
  useRevokeDevice,
  useSettings,
  useSystemActions,
  useSystemStatus,
  useUpdateSettings,
} from '../api/hooks'
import { TIER_LABELS } from '../api/models'
import type { JobOut } from '../api/models'
import {
  Badge,
  EmptyState,
  ErrorState,
  Field,
  LoadingBlock,
  Section,
  formatBytes,
  formatDateTime,
} from '../components/ui'

const TABS = [
  { key: 'mail', label: 'Mailkonten' },
  { key: 'llm', label: 'KI-Provider' },
  { key: 'privacy', label: 'Privacy & Kategorien' },
  { key: 'reminders', label: 'Erinnerungen' },
  { key: 'account', label: 'Konto & Geräte' },
  { key: 'system', label: 'System' },
] as const

type TabKey = (typeof TABS)[number]['key']

export function SettingsPage() {
  const [tab, setTab] = useState<TabKey>('mail')
  return (
    <>
      <h1 className="mb-4 text-2xl">Einstellungen</h1>
      <div role="tablist" aria-label="Bereiche" className="mb-6 flex flex-wrap gap-1">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            className={`btn ${tab === entry.key ? 'btn-secondary' : 'btn-ghost'}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'mail' ? <MailSettings /> : null}
      {tab === 'llm' ? <LlmSettings /> : null}
      {tab === 'privacy' ? <PrivacySettings /> : null}
      {tab === 'reminders' ? <ReminderSettings /> : null}
      {tab === 'account' ? <AccountSettings /> : null}
      {tab === 'system' ? <SystemSettings /> : null}
    </>
  )
}

// --------------------------------------------------------------------- mail

/** Gmail and iCloud both refuse the account password over IMAP. Say that here, once. */
const MAIL_HELP: Record<string, { title: string; steps: string[]; link?: { href: string; label: string }; user: string }> = {
  gmail: {
    title: 'Gmail braucht ein App-Passwort, nicht dein Google-Passwort',
    steps: [
      'Zwei-Faktor-Authentifizierung im Google-Konto aktivieren (ohne sie gibt es keine App-Passwörter).',
      'App-Passwort erzeugen und die 16 Zeichen unten einfügen — Leerzeichen dürfen drin bleiben.',
      'Als Benutzername die vollständige Adresse eintragen, z. B. name@gmail.com.',
    ],
    link: { href: 'https://myaccount.google.com/apppasswords', label: 'App-Passwort bei Google erzeugen' },
    user: 'name@gmail.com',
  },
  icloud: {
    title: 'iCloud braucht ein app-spezifisches Passwort',
    steps: [
      'Bei account.apple.com anmelden, Bereich „Anmeldung und Sicherheit".',
      'Unter „App-spezifische Passwörter" eines erzeugen und unten einfügen.',
      'Benutzername ist die Apple-ID, z. B. name@icloud.com.',
    ],
    link: { href: 'https://account.apple.com/account/manage', label: 'App-spezifisches Passwort bei Apple erzeugen' },
    user: 'name@icloud.com',
  },
  imap: {
    title: 'Generisches IMAP',
    steps: [
      'Host und Port des Providers eintragen (fast immer Port 993 mit TLS).',
      'Benutzername und Passwort sind die des Postfachs.',
    ],
    user: 'name@example.com',
  },
}

function MailKindHelp({ kind }: { kind: string }) {
  const help = MAIL_HELP[kind]
  if (!help) return null
  return (
    <div className="mb-4 rounded-md border border-paper-200 bg-paper-100 p-3 text-sm dark:border-paper-800 dark:bg-paper-800/60">
      <p className="font-medium">{help.title}</p>
      <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-paper-600 dark:text-paper-300">
        {help.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      {help.link ? (
        <a className="mt-2 inline-block underline" href={help.link.href} target="_blank" rel="noreferrer">
          {help.link.label} ↗
        </a>
      ) : null}
      <p className="hint mt-2">
        Backoffice liest Postfächer ausschliesslich lesend — es markiert, verschiebt und
        löscht nichts.
      </p>
    </div>
  )
}

function MailSettings() {
  const accounts = useMailAccounts()
  const actions = useMailAccountActions()
  const [form, setForm] = useState({
    name: '',
    kind: 'gmail',
    username: '',
    secret: '',
    host: '',
    port: '',
    privacy_level: 'local_only',
  })
  const [testResult, setTestResult] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  if (accounts.isPending) return <LoadingBlock />
  if (accounts.isError) return <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />

  return (
    <>
      <Section title="Konten" description="Postfächer werden ausschliesslich lesend geöffnet.">
        {accounts.data.length === 0 ? (
          <EmptyState title="Noch kein Konto" description="Unten ein Mailkonto hinzufügen." />
        ) : (
          <ul className="space-y-2">
            {accounts.data.map((account) => (
              <li key={account.id} className="card">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="min-w-0 flex-1 font-medium">{account.name}</p>
                  <Badge tone={account.enabled ? 'ok' : 'neutral'}>
                    {account.enabled ? 'aktiv' : 'pausiert'}
                  </Badge>
                  <Badge tone={account.privacy_level === 'cloud_ok' ? 'warn' : 'neutral'}>
                    {account.privacy_level === 'cloud_ok' ? 'Cloud erlaubt' : 'nur lokal'}
                  </Badge>
                </div>
                <p className="hint">
                  {account.kind} · {account.username} · {account.host}:{account.port}
                </p>
                <p className="hint">
                  Letzter Sync: {account.last_sync_at ? formatDateTime(account.last_sync_at) : 'noch nie'}
                </p>
                {testResult[account.id] ? (
                  <p className="mt-2 text-sm" role="status">
                    {testResult[account.id]}
                  </p>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={actions.test.isPending}
                    onClick={() =>
                      actions.test.mutate(account.id, {
                        onSuccess: (data) =>
                          setTestResult((current) => ({
                            ...current,
                            [account.id]: `${data.ok ? '✓' : '✕'} ${data.detail} (${(data.folders ?? []).length} Ordner)`,
                          })),
                        onError: (caught) =>
                          setTestResult((current) => ({
                            ...current,
                            [account.id]: errorMessage(caught),
                          })),
                      })
                    }
                  >
                    Verbindung testen
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => actions.sync.mutate(account.id)}
                  >
                    Jetzt synchronisieren
                  </button>
                  <button
                    type="button"
                    className="btn-ghost text-red-700 dark:text-red-300"
                    onClick={() => {
                      if (window.confirm(`Konto „${account.name}" wirklich entfernen?`)) {
                        actions.remove.mutate(account.id)
                      }
                    }}
                  >
                    Entfernen
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Konto hinzufügen">
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            actions.create.mutate(
              {
                name: form.name,
                kind: form.kind,
                username: form.username,
                secret: form.secret,
                host: form.host || null,
                port: form.port ? Number(form.port) : null,
                privacy_level: form.privacy_level,
                auth_type: 'password',
                enabled: true,
              },
              {
                onSuccess: () =>
                  setForm({
                    name: '',
                    kind: 'gmail',
                    username: '',
                    secret: '',
                    host: '',
                    port: '',
                    privacy_level: 'local_only',
                  }),
                onError: (caught) => setError(errorMessage(caught)),
              },
            )
          }}
        >
          {error ? (
            <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </p>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Bezeichnung">
              {(props) => (
                <input
                  {...props}
                  className="input"
                  required
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              )}
            </Field>
            <Field label="Typ">
              {(props) => (
                <select
                  {...props}
                  className="input"
                  value={form.kind}
                  onChange={(event) => setForm({ ...form, kind: event.target.value })}
                >
                  <option value="gmail">Gmail</option>
                  <option value="icloud">iCloud</option>
                  <option value="imap">IMAP</option>
                </select>
              )}
            </Field>
          </div>
          <MailKindHelp kind={form.kind} />
          {form.kind === 'imap' ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Host" hint="z. B. mail.example.com">
                {(props) => (
                  <input
                    {...props}
                    className="input"
                    placeholder="mail.example.com"
                    value={form.host}
                    onChange={(event) => setForm({ ...form, host: event.target.value })}
                  />
                )}
              </Field>
              <Field label="Port" hint="993 für IMAP über TLS">
                {(props) => (
                  <input
                    {...props}
                    className="input"
                    inputMode="numeric"
                    placeholder="993"
                    value={form.port}
                    onChange={(event) => setForm({ ...form, port: event.target.value })}
                  />
                )}
              </Field>
            </div>
          ) : (
            <p className="hint mb-4">
              Host und Port setzt Backoffice automatisch:{' '}
              {form.kind === 'gmail' ? 'imap.gmail.com:993' : 'imap.mail.me.com:993'}.
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Benutzername" hint="Die vollständige E-Mail-Adresse.">
              {(props) => (
                <input
                  {...props}
                  className="input"
                  type="email"
                  autoComplete="off"
                  placeholder={MAIL_HELP[form.kind]?.user}
                  required
                  value={form.username}
                  onChange={(event) => setForm({ ...form, username: event.target.value })}
                />
              )}
            </Field>
            <Field
              label={form.kind === 'imap' ? 'Passwort' : 'App-Passwort'}
              hint={
                form.kind === 'imap'
                  ? 'Wird mit dem Master-Key verschlüsselt gespeichert.'
                  : 'Nicht das normale Kontopasswort — das lehnt der Anbieter ab.'
              }
            >
              {(props) => (
                <input
                  {...props}
                  className="input"
                  type="password"
                  autoComplete="off"
                  required
                  value={form.secret}
                  onChange={(event) => setForm({ ...form, secret: event.target.value })}
                />
              )}
            </Field>
          </div>
          <Field label="Privacy" hint="Nur bei »Cloud erlaubt« dürfen Inhalte an Cloud-Modelle gehen.">
            {(props) => (
              <select
                {...props}
                className="input"
                value={form.privacy_level}
                onChange={(event) => setForm({ ...form, privacy_level: event.target.value })}
              >
                <option value="local_only">nur lokal</option>
                <option value="cloud_ok">Cloud erlaubt</option>
              </select>
            )}
          </Field>
          <button type="submit" className="btn-primary" disabled={actions.create.isPending}>
            Konto anlegen
          </button>
        </form>
      </Section>
    </>
  )
}

// ---------------------------------------------------------------------- llm

function LlmSettings() {
  const providers = useProviders()
  const actions = useProviderActions()
  const lmlink = useLmLink()
  const claude = useClaudeStatus()
  const [form, setForm] = useState({
    name: '',
    kind: 'ollama',
    base_url: 'http://ollama:11434/v1',
    default_model: '',
    tier: 'batch',
    secret: '',
  })
  const [testResult, setTestResult] = useState<Record<string, string>>({})

  return (
    <>
      <Section title="Provider">
        {providers.isPending ? (
          <LoadingBlock />
        ) : providers.isError ? (
          <ErrorState error={providers.error} onRetry={() => void providers.refetch()} />
        ) : providers.data.length === 0 ? (
          <EmptyState
            title="Kein Provider"
            description="Ohne Provider läuft alles weiter — KI-Jobs warten sichtbar in der Warteschlange."
          />
        ) : (
          <ul className="space-y-2">
            {providers.data.map((provider) => (
              <li key={provider.id} className="card">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="min-w-0 flex-1 font-medium">{provider.name}</p>
                  <Badge>{provider.kind}</Badge>
                  <Badge tone={provider.privacy_class === 'cloud_ok' ? 'warn' : 'ok'}>
                    {provider.privacy_class === 'cloud_ok' ? 'Cloud' : 'lokal'}
                  </Badge>
                  <Badge tone={(provider.status as { ok?: boolean })?.ok ? 'ok' : 'neutral'}>
                    {(provider.status as { ok?: boolean })?.ok ? 'online' : 'offline'}
                  </Badge>
                </div>
                <p className="hint">{provider.base_url ?? 'Subprozess'}</p>
                <p className="hint">
                  Modelle:{' '}
                  {Object.entries(provider.model_map as Record<string, string>)
                    .map(([model, tier]) => `${model} → ${TIER_LABELS[tier] ?? tier}`)
                    .join(', ') || '—'}
                </p>
                {(provider.windows as unknown[]).length > 0 ? (
                  <p className="hint">
                    Zeitfenster:{' '}
                    {(provider.windows as { from: string; to: string; mode: string }[])
                      .map((window) => `${window.mode === 'blocked' ? 'gesperrt' : 'erlaubt'} ${window.from}–${window.to}`)
                      .join(' · ')}
                  </p>
                ) : null}
                {testResult[provider.id] ? (
                  <p className="mt-2 text-sm" role="status">
                    {testResult[provider.id]}
                  </p>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() =>
                      actions.test.mutate(provider.id, {
                        onSuccess: (data) =>
                          setTestResult((current) => ({
                            ...current,
                            [provider.id]: `${data.ok ? '✓' : '✕'} ${data.detail} · Modelle: ${
                              (data.models ?? []).join(', ') || '—'
                            }${data.window_open ? '' : ' · Zeitfenster geschlossen'}`,
                          })),
                        onError: (caught) =>
                          setTestResult((current) => ({ ...current, [provider.id]: errorMessage(caught) })),
                      })
                    }
                  >
                    Testen
                  </button>
                  <button
                    type="button"
                    className="btn-ghost text-red-700 dark:text-red-300"
                    onClick={() => {
                      if (window.confirm(`Provider „${provider.name}" entfernen?`)) {
                        actions.remove.mutate(provider.id)
                      }
                    }}
                  >
                    Entfernen
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Provider hinzufügen">
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault()
            actions.create.mutate({
              name: form.name,
              kind: form.kind,
              base_url: form.kind === 'claude_cli' ? null : form.base_url,
              default_model: form.default_model || null,
              secret: form.secret || null,
              model_map: form.default_model
                ? { [form.default_model]: form.tier }
                : { '*': form.tier },
              windows:
                form.tier === 'batch'
                  ? [{ days: [0, 1, 2, 3, 4], from: '07:00', to: '16:00', mode: 'blocked' }]
                  : [],
              enabled: true,
              privacy_class: form.kind === 'claude_cli' ? 'cloud_ok' : 'local_only',
            })
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Bezeichnung">
              {(props) => (
                <input
                  {...props}
                  className="input"
                  required
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              )}
            </Field>
            <Field label="Art">
              {(props) => (
                <select
                  {...props}
                  className="input"
                  value={form.kind}
                  onChange={(event) => setForm({ ...form, kind: event.target.value })}
                >
                  <option value="ollama">Ollama</option>
                  <option value="lmlink">LM Link (llmster)</option>
                  <option value="claude_cli">Claude CLI</option>
                </select>
              )}
            </Field>
          </div>
          {form.kind !== 'claude_cli' ? (
            <Field label="Basis-URL">
              {(props) => (
                <input
                  {...props}
                  className="input"
                  value={form.base_url}
                  onChange={(event) => setForm({ ...form, base_url: event.target.value })}
                />
              )}
            </Field>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Modell">
              {(props) => (
                <input
                  {...props}
                  className="input"
                  value={form.default_model}
                  onChange={(event) => setForm({ ...form, default_model: event.target.value })}
                />
              )}
            </Field>
            <Field label="Tier">
              {(props) => (
                <select
                  {...props}
                  className="input"
                  value={form.tier}
                  onChange={(event) => setForm({ ...form, tier: event.target.value })}
                >
                  <option value="always">Immer</option>
                  <option value="batch">Batch</option>
                  <option value="heavy">Schwer</option>
                  <option value="cloud">Cloud</option>
                </select>
              )}
            </Field>
          </div>
          <Field
            label={form.kind === 'claude_cli' ? 'Claude-CLI-Token' : 'Token (optional)'}
            hint="Wird verschlüsselt gespeichert und nie wieder angezeigt."
          >
            {(props) => (
              <input
                {...props}
                className="input"
                type="password"
                value={form.secret}
                onChange={(event) => setForm({ ...form, secret: event.target.value })}
              />
            )}
          </Field>
          <button type="submit" className="btn-primary" disabled={actions.create.isPending}>
            Provider anlegen
          </button>
        </form>
      </Section>

      <Section
        title="LM Link"
        description="Der llmster-Container ist das Gateway zu den Modellen des Laptops."
      >
        <div className="card">
          {lmlink.status.isError ? (
            <p className="text-sm text-paper-500">
              Der llmster-Container antwortet nicht: {errorMessage(lmlink.status.error)}
            </p>
          ) : lmlink.status.isPending ? (
            <LoadingBlock />
          ) : (
            <>
              <p className="flex items-center gap-2">
                <Badge tone={lmlink.status.data.connected ? 'ok' : 'neutral'}>
                  {lmlink.status.data.connected ? 'verbunden' : 'nicht verbunden'}
                </Badge>
                {lmlink.status.data.device_name ? (
                  <span className="text-sm">{lmlink.status.data.device_name}</span>
                ) : null}
              </p>
              {(lmlink.status.data.models ?? []).length > 0 ? (
                <p className="hint">
                  Sichtbare Modelle: {(lmlink.status.data.models ?? []).join(', ')}
                </p>
              ) : null}
              {lmlink.connect.isError ? (
                <p role="alert" className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950/50 dark:text-red-100">
                  {errorMessage(lmlink.connect.error, 'Verbinden fehlgeschlagen.')}
                </p>
              ) : null}
              {lmlink.connect.data?.login_url ? (
                <div className="mt-3 rounded-md bg-ink-50 p-4 text-sm dark:bg-ink-900/40">
                  <p className="font-medium">Anmeldung bestätigen</p>
                  <p className="mt-1">
                    Seite öffnen:{' '}
                    <a
                      className="underline"
                      href={lmlink.connect.data.login_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {lmlink.connect.data.login_url}
                    </a>
                  </p>
                  {lmlink.connect.data.login_code ? (
                    <p className="mt-2">
                      Code:{' '}
                      <span className="select-all rounded bg-white px-2 py-1 font-mono text-base tracking-wide dark:bg-paper-900">
                        {lmlink.connect.data.login_code}
                      </span>
                    </p>
                  ) : null}
                  <p className="hint mt-2">
                    Der Status oben aktualisiert sich automatisch, sobald die Bestätigung
                    durch ist.
                  </p>
                </div>
              ) : null}
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={lmlink.connect.isPending}
                  onClick={() => lmlink.connect.mutate()}
                >
                  {lmlink.connect.isPending ? 'Wird gestartet…' : 'Verbinden'}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => lmlink.disconnect.mutate()}
                >
                  Trennen
                </button>
              </div>
            </>
          )}
        </div>
      </Section>

      <Section title="Claude CLI">
        <div className="card">
          {claude.isPending ? (
            <LoadingBlock />
          ) : claude.isError ? (
            <ErrorState error={claude.error} />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={claude.data.installed ? 'ok' : 'danger'}>
                  {claude.data.installed ? 'CLI installiert' : 'CLI fehlt'}
                </Badge>
                <Badge tone={claude.data.provider_configured ? 'ok' : 'warn'}>
                  {claude.data.provider_configured ? 'Provider angelegt' : 'kein Provider'}
                </Badge>
                <Badge tone={claude.data.authenticated ? 'ok' : 'warn'}>
                  {claude.data.authenticated ? 'angemeldet' : 'nicht angemeldet'}
                </Badge>
              </div>
              <p className="mt-2 text-sm">{claude.data.detail}</p>
              {claude.data.installed && !claude.data.provider_configured ? (
                <p className="mt-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                  Claude taucht erst in der Provider-Liste und im Systemstatus auf, wenn
                  oben ein Provider der Art <strong>Claude CLI</strong> angelegt ist. Dass
                  die CLI auf dem Server läuft, reicht dem Router nicht — er wählt nur aus,
                  was in der Datenbank steht.
                </p>
              ) : null}
              <p className="hint mt-2">
                Inhalte erreichen Claude nur, wenn Konto oder Kategorie ausdrücklich
                »Cloud erlaubt« sind.
              </p>
            </>
          )}
        </div>
      </Section>
    </>
  )
}

// ------------------------------------------------------------------ privacy

function PrivacySettings() {
  const settings = useSettings()
  const update = useUpdateSettings()
  const categories = useCategories()

  if (settings.isPending) return <LoadingBlock />
  if (settings.isError) return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />

  const defaultLevel = (settings.data['privacy.default_level'] as string) ?? 'local_only'

  return (
    <>
      <Section title="Privacy" description="Standard ist »nur lokal«. Cloud-Modelle sind die Ausnahme.">
        <div className="card">
          <Field label="Standard für neue Dokumente">
            {(props) => (
              <select
                {...props}
                className="input"
                value={defaultLevel}
                onChange={(event) =>
                  update.mutate({ 'privacy.default_level': event.target.value })
                }
              >
                <option value="local_only">nur lokal</option>
                <option value="cloud_ok">Cloud erlaubt</option>
              </select>
            )}
          </Field>
          <p className="hint">
            Konten können den Standard überschreiben. Ein Dokument mit „nur lokal" erreicht
            nachweislich nie den Claude-Provider.
          </p>
        </div>
      </Section>

      <Section title="Kategorien">
        {categories.isPending ? (
          <LoadingBlock />
        ) : categories.isError ? (
          <ErrorState error={categories.error} />
        ) : (
          <ul className="card grid gap-2 sm:grid-cols-2">
            {categories.data.map((category) => (
              <li key={category.id} className="flex items-center justify-between gap-2">
                <span>{category.label_de}</span>
                <Badge tone={category.privacy_level === 'cloud_ok' ? 'warn' : 'neutral'}>
                  {category.privacy_level === 'cloud_ok' ? 'Cloud erlaubt' : 'nur lokal'}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  )
}

// ---------------------------------------------------------------- reminders

function ReminderSettings() {
  const settings = useSettings()
  const update = useUpdateSettings()
  const [offsets, setOffsets] = useState<string | null>(null)

  if (settings.isPending) return <LoadingBlock />
  if (settings.isError) return <ErrorState error={settings.error} />

  const current = (settings.data['reminders.offsets_days'] as number[]) ?? [7, 3, 1]
  const value = offsets ?? current.join(', ')

  return (
    <Section title="Erinnerungen" description="Vorlauf vor einer Frist, in Tagen.">
      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault()
          const parsed = value
            .split(',')
            .map((entry) => Number.parseInt(entry.trim(), 10))
            .filter((entry) => Number.isFinite(entry) && entry >= 0)
          update.mutate({ 'reminders.offsets_days': parsed })
        }}
      >
        <Field label="Vorlauf (Tage)" hint="Komma-getrennt, z. B. 7, 3, 1">
          {(props) => (
            <input
              {...props}
              className="input"
              value={value}
              onChange={(event) => setOffsets(event.target.value)}
            />
          )}
        </Field>
        <Field label="Uhrzeit der Erinnerung">
          {(props) => (
            <input
              {...props}
              className="input"
              type="number"
              min={0}
              max={23}
              value={(settings.data['reminders.due_hour'] as number) ?? 9}
              onChange={(event) =>
                update.mutate({ 'reminders.due_hour': Number(event.target.value) })
              }
            />
          )}
        </Field>
        <button type="submit" className="btn-primary" disabled={update.isPending}>
          Speichern
        </button>
        <p className="hint mt-2">
          Benachrichtigungen laufen ausschliesslich über die nativen Apps. Bis die existieren,
          sind fällige Erinnerungen hier, über die API und in der CLI sichtbar.
        </p>
      </form>
    </Section>
  )
}

// ------------------------------------------------------------------ account

function AccountSettings() {
  const devices = useDevices()
  const revoke = useRevokeDevice()
  const changePassword = useChangePassword()
  const [form, setForm] = useState({ current: '', next: '', repeat: '' })
  const [message, setMessage] = useState<string | null>(null)

  return (
    <>
      <Section title="Passwort">
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault()
            setMessage(null)
            if (form.next !== form.repeat) {
              setMessage('Die Passwörter stimmen nicht überein.')
              return
            }
            changePassword.mutate(
              { current_password: form.current, new_password: form.next },
              {
                onSuccess: () => setMessage('Passwort geändert. Bitte erneut anmelden.'),
                onError: (caught) => setMessage(errorMessage(caught)),
              },
            )
          }}
        >
          {message ? (
            <p role="status" className="mb-3 text-sm">
              {message}
            </p>
          ) : null}
          <Field label="Aktuelles Passwort">
            {(props) => (
              <input
                {...props}
                className="input"
                type="password"
                autoComplete="current-password"
                required
                value={form.current}
                onChange={(event) => setForm({ ...form, current: event.target.value })}
              />
            )}
          </Field>
          <Field label="Neues Passwort" hint="Mindestens 12 Zeichen.">
            {(props) => (
              <input
                {...props}
                className="input"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={form.next}
                onChange={(event) => setForm({ ...form, next: event.target.value })}
              />
            )}
          </Field>
          <Field label="Wiederholen">
            {(props) => (
              <input
                {...props}
                className="input"
                type="password"
                autoComplete="new-password"
                required
                value={form.repeat}
                onChange={(event) => setForm({ ...form, repeat: event.target.value })}
              />
            )}
          </Field>
          <button type="submit" className="btn-primary" disabled={changePassword.isPending}>
            Passwort ändern
          </button>
        </form>
      </Section>

      <Section title="Geräte und Sitzungen" description="Jedes Gerät lässt sich einzeln abmelden.">
        {devices.isPending ? (
          <LoadingBlock />
        ) : devices.isError ? (
          <ErrorState error={devices.error} />
        ) : (
          <ul className="space-y-2">
            {devices.data.map((device) => (
              <li key={device.id} className="card flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{device.name || device.platform}</p>
                  <p className="hint">
                    {device.platform} · {device.active_sessions} aktive Sitzung(en) · zuletzt{' '}
                    {formatDateTime(device.last_seen_at)}
                  </p>
                </div>
                {device.push_registered ? <Badge tone="ok">Push</Badge> : null}
                <button
                  type="button"
                  className="btn-ghost text-red-700 dark:text-red-300"
                  onClick={() => revoke.mutate(device.id)}
                >
                  Abmelden
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  )
}

// ------------------------------------------------------------------- system

function SystemSettings() {
  const status = useSystemStatus(5000)
  const jobs = useJobs()
  const actions = useSystemActions()

  return (
    <>
      <Section title="Warteschlange">
        {status.isPending ? (
          <LoadingBlock />
        ) : status.isError ? (
          <ErrorState error={status.error} />
        ) : (
          <div className="card">
            <p className="text-sm">
              Tiefe {String(status.data.queue.depth)} · {String(status.data.queue.waiting_for_model ?? 0)}{' '}
              warten auf ein Modell
            </p>
            <p className="hint">Speicherbelegung: {formatBytes(status.data.storage_bytes)}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => actions.requeueStale.mutate()}
              >
                Hängende Jobs zurückstellen
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  if (window.confirm('Alle Dokumente neu verarbeiten?')) actions.reprocess.mutate(undefined)
                }}
              >
                Alles neu verarbeiten
              </button>
            </div>
          </div>
        )}
      </Section>

      <Section title="Jobs">
        {jobs.isPending ? (
          <LoadingBlock />
        ) : jobs.isError ? (
          <ErrorState error={jobs.error} />
        ) : (jobs.data as JobOut[]).length === 0 ? (
          <EmptyState title="Keine Jobs" />
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Zuletzt eingereihte Jobs</caption>
              <thead>
                <tr className="text-left text-paper-500">
                  <th scope="col" className="py-1">Art</th>
                  <th scope="col" className="py-1">Status</th>
                  <th scope="col" className="py-1">Tiers</th>
                  <th scope="col" className="py-1">Versuche</th>
                  <th scope="col" className="py-1" />
                </tr>
              </thead>
              <tbody>
                {(jobs.data as JobOut[]).slice(0, 25).map((job) => (
                  <tr key={job.id} className="border-t border-paper-200 align-top dark:border-paper-800">
                    <td className="py-1">
                      {job.kind}
                      {/* A job that keeps retrying looks like it hangs unless the reason
                          is on screen. */}
                      {job.last_error ? (
                        <p className="mt-0.5 max-w-md break-words text-xs text-red-700 dark:text-red-300">
                          {job.last_error.slice(0, 220)}
                        </p>
                      ) : null}
                    </td>
                    <td className="py-1">
                      <Badge
                        tone={
                          job.status === 'failed'
                            ? 'danger'
                            : job.status === 'done'
                              ? 'ok'
                              : job.status === 'running'
                                ? 'accent'
                                : 'neutral'
                        }
                      >
                        {job.status}
                      </Badge>
                    </td>
                    <td className="py-1 text-xs">{(job.allow_tiers as string[]).join(', ') || '—'}</td>
                    <td className="py-1">{job.attempts}</td>
                    <td className="py-1 text-right">
                      {job.status !== 'done' ? (
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          onClick={() => actions.retryJob.mutate(job.id)}
                        >
                          Jetzt
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </>
  )
}
